import type { UserSession } from 'api/generated'

/** What we persist in localStorage after a backoffice login. */
export type SavedUser = UserSession & { user_id: number }

const COOKIE_KEY = `${import.meta.env.VITE_APP_ENV || 'development'}_tennistalker_current_user`

const getCurrentUser = (): SavedUser | null =>
  typeof window === 'undefined'
    ? null
    : JSON.parse(localStorage.getItem(COOKIE_KEY) as string)

export const isBannerManager = () => {
  const u = getCurrentUser()
  return u?.role === 'banner_manager'
}

export const isCensorManager = () => {
  const u = getCurrentUser()
  return u?.role === 'censor_manager'
}

// Compared on every call (no memoization): a token that expires while the
// backoffice is open must be detected without a page reload.
const isExpired = (tokenExpiration: string | null): boolean =>
  !tokenExpiration || new Date(tokenExpiration) < new Date()

export const logoutFrontend = () => {
  unsetCurrentUser()
  window.location.href = '/accedi'
}

export const setCurrentUser = (user: SavedUser) =>
  localStorage.setItem(COOKIE_KEY, JSON.stringify(user))

export const unsetCurrentUser = () => localStorage.removeItem(COOKIE_KEY)

export const currentUser = (): SavedUser | null => {
  const user = getCurrentUser()
  if (user) {
    if (isExpired(user.token_expiration)) {
      unsetCurrentUser()
      return null
    }
    return user
  }
  return user
}

export const isLoggedIn = () => {
  const user = currentUser()
  return !!user && !isExpired(user.token_expiration)
}

export const getAPIToken = () => currentUser()?.token ?? ''

export const isCurrentUser = (userId?: number) =>
  userId !== undefined && currentUser()?.id === userId
